<template>
  <div>
    <b-row class="match-height">
      <!-- Contact Information: Left col -->
      <b-col
        cols="12"
        xl="6"
        lg="6"
        md="12"
      >
        <b-card class="offer-card h-100">
          <!-- Offer Header -->
          <div class="offer-header">
            <div class="offer-salutation">
              {{ offerEntry.salutation }}
            </div>
            <h4 class="offer-name">
              {{ offerEntry.firstName }} {{ offerEntry.lastName }}
            </h4>
            <div class="offer-meta">
              <div class="offer-id">
                <feather-icon
                  icon="HashIcon"
                  size="16"
                />
                Angebot #{{ offerEntry.id }}
              </div>
              <div class="offer-email">
                <feather-icon
                  icon="MailIcon"
                  size="16"
                />
                <a
                  :href="`mailto:${offerEntry.email}`"
                  class="email-link"
                >
                  {{ offerEntry.email }}
                </a>
              </div>
            </div>
          </div>

          <!-- Contact Information -->
          <div class="contact-section">
            <div
              v-if="offerEntry.company"
              class="info-row"
            >
              <div class="info-label">
                Unternehmen
              </div>
              <div class="info-value">
                {{ offerEntry.company }}
              </div>
            </div>

            <div class="info-row">
              <div class="info-label">
                Strasse
              </div>
              <div class="info-value">
                {{ offerEntry.street }} {{ offerEntry.streetNumber }}
              </div>
            </div>

            <div class="info-row">
              <div class="info-label">
                PLZ | Stadt
              </div>
              <div class="info-value">
                {{ offerEntry.zipCode }} {{ offerEntry.city }}
              </div>
            </div>

            <div class="info-row">
              <div class="info-label">
                Land
              </div>
              <div class="info-value">
                {{ displayCountry }}
              </div>
            </div>

            <div
              v-if="offerEntry.phoneNumber"
              class="info-row"
            >
              <div class="info-label">
                Telefon
              </div>
              <div class="info-value">
                <a
                  :href="`tel:${offerEntry.phoneNumber}`"
                  class="phone-link"
                >
                  {{ offerEntry.phoneNumber }}
                </a>
              </div>
            </div>

            <div class="info-row">
              <div class="info-label">
                Mobile
              </div>
              <div class="info-value">
                <a
                  v-if="displayMobileNumber"
                  :href="`tel:${displayMobileNumber}`"
                  class="phone-link"
                >
                  {{ displayMobileNumber }}
                </a>
                <span v-else>-</span>
              </div>
            </div>

            <div
              v-if="offerEntry.faxNumber"
              class="info-row"
            >
              <div class="info-label">
                Fax
              </div>
              <div class="info-value">
                {{ offerEntry.faxNumber }}
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- Offer Status & Details: Right col -->
      <b-col
        cols="12"
        xl="6"
        lg="6"
        md="12"
      >
        <b-card class="status-card h-100">
          <!-- Status Section -->
          <div class="section-header">
            <feather-icon
              icon="FileTextIcon"
              size="20"
              class="section-icon"
            />
            <h5 class="section-title">
              Angebot Details
            </h5>
          </div>

          <div class="status-section">
            <div class="info-row">
              <div class="info-label">
                Status
              </div>
              <div class="info-value">
                <span
                  :class="offerEntry.isActive ? 'status-badge active-badge' : 'status-badge inactive-badge'"
                >
                  {{ offerEntry.isActive ? 'Aktiv' : 'Deaktiviert' }}
                </span>
              </div>
            </div>

            <div class="info-row">
              <div class="info-label">
                Erstellt
              </div>
              <div class="info-value">
                {{ getFormattedDate(offerEntry.created_at) }}
              </div>
            </div>

            <div class="info-row">
              <div class="info-label">
                Leadquelle
              </div>
              <div class="info-value">
                {{ offerEntry.leadSource || 'Unbekannt' }}
              </div>
            </div>
          </div>

          <!-- Actions Section -->
          <div class="actions-section">
            <div class="section-header">
              <feather-icon
                icon="SettingsIcon"
                size="20"
                class="section-icon"
              />
              <h5 class="section-title">
                Aktionen
              </h5>
            </div>

            <div class="action-buttons">
              <b-button
                variant="primary"
                class="action-btn"
                @click="$router.push('/inquiries-view/' + offerEntry.inquiry.id)"
              >
                <feather-icon
                  icon="ExternalLinkIcon"
                  size="16"
                  class="mr-50"
                />
                Anfrage öffnen
              </b-button>

              <b-button
                variant="danger"
                class="action-btn"
                :disabled="isDisabled()"
                @click="isActiveSwitch"
              >
                <feather-icon
                  icon="XCircleIcon"
                  size="16"
                  class="mr-50"
                />
                Angebot deaktivieren
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      ref="modal-isActive"
      centered
      title="Angebot deaktivieren?"
      ok-title="Ok"
      cancel-title="Abbrechen"
      cancel-variant="outline-secondary"
      :no-close-on-backdrop="true"
      @ok="deactivateOfferSubmitted"
    >
      Angebot wirklich deaktivieren? Angebot kann nicht wieder aktiviert werden
      und alle reservierten Lagerplätze werden freigegeben.

      <b-form>
        <b-form-group label-for="vue-select">
          <!-- <v-select
            v-model="this.selectedOption"
            :clearable="false"
            :options="this.qualificationOptions"
            placeholder="Auswahl"
            @option:selected="onChange($event)"
          /> -->
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BCardText,
  BButton,
  BFormCheckbox,
  BBadge,
  VBModal,
  BModal,
  BForm,
  BFormGroup,
} from 'bootstrap-vue'
import helper from '@/helper/helper'

export default {
  setup() {
    const { getFormattedDate } = helper()

    return { getFormattedDate }
  },
  components: {
    BCard,
    BRow,
    BCol,
    BCardText,
    BButton,
    BFormCheckbox,
    BBadge,
    VBModal,
    BModal,
    BForm,
    BFormGroup,
  },
  props: {
    offerEntry: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {}
  },
  computed: {
    displayCountry() {
      // First, try the offer's country directly
      if (this.offerEntry.country) {
        return this.offerEntry.country
      }

      // If offer doesn't have country, check if inquiry has country data directly
      const { inquiry } = this.offerEntry
      if (inquiry && inquiry.country) {
        return inquiry.country
      }

      return '-'
    },
    displayMobileNumber() {
      // First, try the offer's mobile number directly
      if (this.offerEntry.mobileNumber) {
        return this.offerEntry.mobileNumber
      }

      // If offer doesn't have mobile, check if inquiry has mobile data directly
      const { inquiry } = this.offerEntry
      if (inquiry && inquiry.mobileNumber) {
        return inquiry.mobileNumber
      }

      return null
    },
  },
  methods: {
    isActiveSwitch() {
      // this.$emit('toggleactiveSwitch')
      this.$refs['modal-isActive'].show()
    },
    async deactivateOfferSubmitted() {
      try {
        // 1. Update storages - set storageReservation to false and clear dates
        const storageUpdates = this.offerEntry.storages.map(storage => this.$store.dispatch('updateStorage', {
          id: storage.id,
          storageReservation: false,
          rentStart: null,
          rentEnd: null,
          // Convert relations to IDs
          location: storage.location?.id || storage.location,
          storage_type: storage.storage_type?.id || storage.storage_type,
          // Include other scalar fields
          storageID: storage.storageID,
          name: storage.name,
          price: storage.price,
          discount: storage.discount,
          lockPin: storage.lockPin,
          storageSpace: storage.storageSpace,
          storageVolume: storage.storageVolume
        }))

        // 2. Find and update related OrderItems
        const orderItems = this.$store.getters.orderItemsEntries || []
        const relatedOrderItems = orderItems.filter(item => this.offerEntry.storages.some(storage => item.storage_id === storage.id || item.storage?.id === storage.id))

        console.log(`Found ${relatedOrderItems.length} related OrderItems to deactivate`)

        // Set deactivation date on OrderItems (if they exist)
        const orderItemUpdates = relatedOrderItems.map(item => this.$store.dispatch('updateOrderItem', {
          ...item,
          offerDeactivated: true,
          offerDeactivationDate: new Date().toISOString()
        }))

        // 3. Prepare clean offer payload (without nested objects)
        const cleanOfferPayload = {
          id: this.offerEntry.id,
          firstName: this.offerEntry.firstName,
          lastName: this.offerEntry.lastName,
          email: this.offerEntry.email,
          salutation: this.offerEntry.salutation,
          street: this.offerEntry.street,
          streetNumber: this.offerEntry.streetNumber,
          city: this.offerEntry.city,
          zipCode: this.offerEntry.zipCode,
          phoneNumber: this.offerEntry.phoneNumber,
          mobileNumber: this.offerEntry.mobileNumber,
          billingAddressDiffers: this.offerEntry.billingAddressDiffers,
          storages: this.offerEntry.storages.map(s => s.id), // Only IDs, not full objects
          offerDate: this.offerEntry.offerDate,
          country: this.offerEntry.country,
          isActive: false, // Set to false for deactivation
          inquiry: this.offerEntry.inquiry,
          bookingCompleted: this.offerEntry.bookingCompleted,
          customer: this.offerEntry.customer
        }

        // 4. Execute all updates
        await Promise.all([
          ...storageUpdates,
          ...orderItemUpdates,
          this.$store.dispatch('updateOffer', cleanOfferPayload)
        ])

        // 5. Refresh data including storage entries to update availability
        await Promise.all([
          this.$store.dispatch('getOfferEntries'),
          this.$store.dispatch('getOrderItemsEntries'),
          this.$store.dispatch('getStorageEntries')
        ])

        console.log('Offer deactivated successfully')
      } catch (error) {
        console.error('Error deactivating offer:', error)
      }
    },
    isDisabled() {
      if (this.offerEntry.isActive === false) {
        return true
      }
      return false
    },
  },
}
</script>
<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.offer-card, .status-card {
  border: 1px solid #e9ecef;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  .card-body {
    padding: 2rem;
  }
}

.offer-header {
  margin-bottom: 2rem;

  .offer-salutation {
    font-size: 0.8rem;
    color: #8a92b2;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .offer-name {
    font-size: 1.75rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  .offer-meta {
    .offer-id {
      display: inline-flex;
      align-items: center;
      background: #f8f9fa;
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-size: 0.875rem;
      color: #495057;
      margin-bottom: 0.75rem;
      margin-right: 1rem;

      .feather {
        margin-right: 0.5rem;
        color: #6c757d;
      }
    }

    .offer-email {
      display: flex;
      align-items: center;
      color: #6c757d;
      font-size: 1rem;
      margin-top: 0.25rem;

      .feather {
        margin-right: 0.5rem;
        color: #007bff;
      }

      .email-link {
        color: #007bff;
        text-decoration: none;
        font-weight: 500;

        &:hover {
          color: #0056b3;
          text-decoration: underline;
        }
      }
    }
  }
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;

  .section-icon {
    color: #007bff;
    margin-right: 0.75rem;
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
  }
}

.contact-section, .status-section {
  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0.875rem 0;
    border-bottom: 1px solid #f1f3f4;

    &:last-child {
      border-bottom: none;
    }

    .info-label {
      font-size: 0.875rem;
      color: #8a92b2;
      font-weight: 500;
      min-width: 100px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .info-value {
      font-size: 1rem;
      color: #2c3e50;
      font-weight: 500;
      text-align: right;
      flex: 1;

      .phone-link {
        color: #007bff;
        text-decoration: none;

        &:hover {
          color: #0056b3;
          text-decoration: underline;
        }
      }

      .status-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.375rem 0.75rem;
        border-radius: 20px;
        font-size: 0.875rem;
        font-weight: 500;

        &.active-badge {
          background: #d4edda;
          color: #155724;
        }

        &.inactive-badge {
          background: #f8d7da;
          color: #721c24;
        }
      }
    }
  }
}

.actions-section {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #f1f3f4;

  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    .action-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.75rem 1.5rem;
      border-radius: 8px;
      font-weight: 500;
      transition: all 0.2s ease;

      .feather {
        margin-right: 0.5rem;
      }

      &:not(:disabled):hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
    }
  }
}

// Deprecated styles for backwards compatibility
hr.style1 {
  border-top: 1px solid #8c8b8b;
}
hr.style5 {
  background-color: #fff;
  border-top: 2px dashed #8c8b8b;
}
</style>
